import { useMemo } from 'react';
import { Link } from 'react-router';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, FileText, Mic, Target,
  Trophy, Flame, Award, ArrowRight, BarChart3, Clock, Star, Zap, Calendar,
  Minus,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useSimulaData } from '../hooks/useSimulaData';
import { useAuth } from '../context/AuthContext';
import { isConcludedAuditStatus } from '../utils/auditStatus';

export function OperatorDashboard() {
  const { user } = useAuth();
  const operatorId = user?.id || '';

  const {
    operators: operatorsEntity,
    audits: auditsEntity,
    trainingSessions: trainingsEntity,
    scorecard: scorecardEntity,
    goals: goalsEntity,
    certificates: certificatesEntity,
    leagueParticipants: leagueParticipantsEntity,
    leagues: leaguesEntity,
    scenarios: scenariosEntity,
    operatorStats,
    getGoalsByOperator,
  } = useSimulaData();

  const operators = operatorsEntity.data;
  const audits = auditsEntity.data;
  const trainingSessions = trainingsEntity.data;
  const scorecard = scorecardEntity.data;
  const goals = goalsEntity.data;
  const certificates = certificatesEntity.data;
  const leagueParticipants = leagueParticipantsEntity.data;
  const leagues = leaguesEntity.data;
  const scenarios = scenariosEntity.data;

  const operator = operators.find(o => o.id === operatorId);
  const stats = operatorStats[operatorId as keyof typeof operatorStats];

  const myAudits = useMemo(() =>
    audits
      .filter(a => a.operatorId === operatorId && isConcludedAuditStatus(a.status) && a.evaluation)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [audits, operatorId]
  );

  const myTrainings = useMemo(() =>
    trainingSessions
      .filter(t => t.operatorId === operatorId && t.evaluation)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [trainingSessions, operatorId]
  );

  const myCerts = useMemo(() =>
    certificates.filter(c => c.operatorId === operatorId),
    [certificates, operatorId]
  );

  const myGoals = useMemo(() =>
    getGoalsByOperator(operatorId),
    [getGoalsByOperator, operatorId]
  );

  const myLeagueParticipation = useMemo(() =>
    leagueParticipants.find(lp => lp.operatorId === operatorId),
    [leagueParticipants, operatorId]
  );

  const currentLeague = useMemo(() =>
    myLeagueParticipation ? leagues.find(l => l.id === myLeagueParticipation.leagueId) : undefined,
    [myLeagueParticipation, leagues]
  );

  // Score trend data for chart
  const scoreTrend = useMemo(() => {
    const allItems = [
      ...myAudits.map(a => ({ date: a.date, score: a.evaluation?.score || 0, type: 'Auditoria' })),
      ...myTrainings.map(t => ({ date: t.date, score: t.evaluation?.score || 0, type: 'Simulacao' })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return allItems.slice(-12).map(item => ({
      date: new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
      score: item.score,
      type: item.type,
    }));
  }, [myAudits, myTrainings]);

  const fallbackStats = useMemo(() => {
    const avgAuditScore = myAudits.length > 0
      ? Math.round(myAudits.reduce((sum, audit) => sum + (audit.evaluation?.score || 0), 0) / myAudits.length)
      : 0;
    const avgTrainingScore = myTrainings.length > 0
      ? Math.round(myTrainings.reduce((sum, training) => sum + (training.evaluation?.score || 0), 0) / myTrainings.length)
      : 0;
    const totalSessions = myAudits.length + myTrainings.length;
    const zeroSessions = [...myAudits, ...myTrainings].filter((item) => item.evaluation?.score === 0).length;

    return {
      avgTrainingScore,
      avgAuditScore,
      totalTrainings: myTrainings.length,
      totalSessions,
      totalAudits: myAudits.length,
      falhaGraveCount: myAudits.filter((audit) => audit.evaluation?.hasFalhaGrave).length,
      zeroScorePercentage: totalSessions > 0 ? Math.round((zeroSessions / totalSessions) * 100) : 0,
      lastActivityDate: myAudits[0]?.date || myTrainings[0]?.date || '',
      trend: 'stable' as const,
    };
  }, [myAudits, myTrainings]);

  const dashboardStats = stats || fallbackStats;

  const trendIcon = dashboardStats.trend === 'up' ? <TrendingUp className="w-4 h-4" /> :
    dashboardStats.trend === 'down' ? <TrendingDown className="w-4 h-4" /> :
    <Minus className="w-4 h-4" />;

  const trendColor = dashboardStats.trend === 'up' ? 'text-[var(--success)]' :
    dashboardStats.trend === 'down' ? 'text-[var(--error)]' :
    'text-muted-foreground';
  const operatorDisplayName = (operator?.name || user?.name || 'Operador').split(' ')[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>
            Ola, {operatorDisplayName}!
          </h1>
          <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
            Aqui esta o resumo do seu desempenho
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/centro-treinamento"
            className="flex items-center gap-2 px-4 py-2 rounded-[var(--radius-button)] bg-secondary text-secondary-foreground hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
          >
            <Mic className="w-4 h-4" />
            Treinar Agora
          </Link>
          <Link
            to="/meu-perfil"
            className="flex items-center gap-2 px-4 py-2 rounded-[var(--radius-button)] border border-border text-foreground hover:bg-muted transition-colors"
            style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
          >
            <BarChart3 className="w-4 h-4" />
            Meu Perfil
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Avg Audit Score */}
        <div data-testid="operator-kpi-audits" className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Media Auditorias
            </span>
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <div className="flex items-baseline gap-2">
            <span data-testid="operator-kpi-audits-score-value" className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>
              {dashboardStats.avgAuditScore || 0}
            </span>
            <span className={`flex items-center gap-1 ${trendColor}`} style={{ fontSize: 'var(--text-xs)' }}>
              {trendIcon}
            </span>
          </div>
          <p data-testid="operator-kpi-audits-total-value" className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
            {dashboardStats.totalAudits || 0} auditorias avaliadas
          </p>
        </div>

        {/* Avg Training Score */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Media Simulacoes
            </span>
            <Mic className="w-4 h-4 text-secondary" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>
              {dashboardStats.avgTrainingScore || 0}
            </span>
          </div>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
            {dashboardStats.totalTrainings || 0} simulacoes concluidas
          </p>
        </div>

        {/* Falha Grave */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Falhas Graves
            </span>
            <AlertTriangle className="w-4 h-4 text-[var(--warning)]" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>
              {dashboardStats.falhaGraveCount || 0}
            </span>
          </div>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
            {dashboardStats.zeroScorePercentage || 0}% notas zeradas
          </p>
        </div>

        {/* League Position */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Liga / Streak
            </span>
            <Trophy className="w-4 h-4 text-[var(--warning)]" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>
              {currentLeague?.name || 'N/A'}
            </span>
          </div>
          <p className="text-muted-foreground mt-1 flex items-center gap-1" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
            <Flame className="w-3 h-3 text-[var(--warning)]" />
            {myLeagueParticipation?.streak || 0} dias de streak
          </p>
        </div>
      </div>

      {/* Chart + Goals Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Trend Chart */}
        <div className="lg:col-span-2 bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <h3 className="text-foreground mb-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
            Evolucao de Notas
          </h3>
          {scoreTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={scoreTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'var(--font-family-inter)', fill: 'var(--muted-foreground)' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: 'var(--font-family-inter)', fill: 'var(--muted-foreground)' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card)',
                    borderColor: 'var(--border)',
                    borderRadius: 'var(--radius)',
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--foreground)',
                  }}
                />
                <Line type="monotone" dataKey="score" stroke="var(--primary)" strokeWidth={2} dot={{ r: 3, fill: 'var(--primary)' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
              Nenhum dado de desempenho disponivel.
            </div>
          )}
        </div>

        {/* Goals */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
              Minhas Metas
            </h3>
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div className="space-y-3">
            {myGoals.slice(0, 4).map(goal => {
              const progress = Math.min(100, Math.round(((goal.current ?? 0) / goal.target) * 100));
              const isCompleted = progress >= 100;
              return (
                <div key={goal.id} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground truncate" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                      {goal.title || goal.name}
                    </span>
                    <span className={isCompleted ? 'text-[var(--success)]' : 'text-muted-foreground'} style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: isCompleted ? 'var(--success)' : 'var(--primary)',
                      }}
                    />
                  </div>
                </div>
              );
            })}
            {myGoals.length === 0 && (
              <p className="text-muted-foreground text-center py-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
                Nenhuma meta atribuida.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Audits + Training */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Audits */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
              Auditorias Recentes
            </h3>
            <Link to="/auditorias" className="text-primary flex items-center gap-1 hover:underline" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Ver todas <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {myAudits.slice(0, 5).map(audit => {
              const hasFG = audit.evaluation?.hasFalhaGrave;
              const score = audit.evaluation?.score || 0;
              const item = scorecard.find(s => s.id === audit.scorecardItemId);
              return (
                <Link
                  key={audit.id}
                  to={`/auditorias/${audit.id}`}
                  className="flex items-center justify-between p-3 rounded-[var(--radius)] border border-border hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      hasFG ? 'bg-[var(--error-muted)]' : score >= 80 ? 'bg-[color-mix(in_srgb,var(--success)_10%,transparent)]' : 'bg-[var(--destructive-muted)]'
                    }`}>
                      {hasFG ? (
                        <AlertTriangle className="w-4 h-4 text-[var(--error)]" />
                      ) : score >= 80 ? (
                        <CheckCircle className="w-4 h-4 text-[var(--success)]" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[var(--warning)]" />
                      )}
                    </div>
                    <div>
                      <p className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                        {item?.name || audit.type}
                      </p>
                      <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                        {new Date(audit.date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={hasFG ? 'text-[var(--error)]' : score >= 80 ? 'text-[var(--success)]' : 'text-[var(--warning)]'}
                      style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}
                    >
                      {hasFG ? 0 : score}
                    </span>
                    {hasFG && (
                      <p className="text-[var(--error)]" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                        Falha Grave
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
            {myAudits.length === 0 && (
              <p className="text-muted-foreground text-center py-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
                Nenhuma auditoria avaliada ainda.
              </p>
            )}
          </div>
        </div>

        {/* Recent Trainings */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
              Simulacoes Recentes
            </h3>
            <Link to="/centro-treinamento" className="text-primary flex items-center gap-1 hover:underline" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
              Treinar <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {myTrainings.slice(0, 5).map(training => {
              const score = training.evaluation?.score || 0;
              const scenario = scenarios.find(s => s.id === training.scenarioId);
              return (
                <div
                  key={training.id}
                  className="flex items-center justify-between p-3 rounded-[var(--radius)] border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[color-mix(in_srgb,var(--secondary)_10%,transparent)]">
                      <Mic className="w-4 h-4 text-secondary" />
                    </div>
                    <div>
                      <p className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                        {scenario?.title || 'Simulacao'}
                      </p>
                      <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                        {new Date(training.date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <span
                    className={score >= 80 ? 'text-[var(--success)]' : score >= 60 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}
                    style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}
                  >
                    {score}
                  </span>
                </div>
              );
            })}
            {myTrainings.length === 0 && (
              <p className="text-muted-foreground text-center py-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
                Nenhuma simulacao concluida ainda.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Certificates + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Certificates */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
              Certificados
            </h3>
            <Award className="w-5 h-5 text-[var(--warning)]" />
          </div>
          <div className="space-y-2">
            {myCerts.slice(0, 4).map(cert => (
              <div key={cert.id} className="flex items-center justify-between p-3 rounded-[var(--radius)] border border-border">
                <div className="flex items-center gap-3">
                  <Star className="w-4 h-4 text-[var(--warning)]" />
                  <div>
                    <p className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                      {cert.name}
                    </p>
                    <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                      {cert.type === 'training' ? 'Treinamento' : cert.type === 'audit' ? 'Auditoria' : 'Combinado'}
                    </p>
                  </div>
                </div>
                <span className="text-muted-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)' }}>
                  {cert.issuedAt ? new Date(cert.issuedAt).toLocaleDateString('pt-BR') : '-'}
                </span>
              </div>
            ))}
            {myCerts.length === 0 && (
              <p className="text-muted-foreground text-center py-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)' }}>
                Nenhum certificado conquistado ainda.
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-[var(--radius-card)] border border-border p-5" style={{ boxShadow: 'var(--elevation-sm)' }}>
          <h3 className="text-foreground mb-4" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
            Acoes Rapidas
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/centro-treinamento"
              className="flex flex-col items-center gap-2 p-4 rounded-[var(--radius)] border border-border hover:bg-muted transition-colors text-center"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color-mix(in_srgb,var(--secondary)_10%,transparent)]">
                <Mic className="w-5 h-5 text-secondary" />
              </div>
              <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                Nova Simulacao
              </span>
            </Link>
            <Link
              to="/auditorias"
              className="flex flex-col items-center gap-2 p-4 rounded-[var(--radius)] border border-border hover:bg-muted transition-colors text-center"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                Auditorias
              </span>
            </Link>
            <Link
              to="/ranking"
              className="flex flex-col items-center gap-2 p-4 rounded-[var(--radius)] border border-border hover:bg-muted transition-colors text-center"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color-mix(in_srgb,var(--warning)_10%,transparent)]">
                <Trophy className="w-5 h-5 text-[var(--warning)]" />
              </div>
              <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                Ranking
              </span>
            </Link>
            <Link
              to="/metas"
              className="flex flex-col items-center gap-2 p-4 rounded-[var(--radius)] border border-border hover:bg-muted transition-colors text-center"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color-mix(in_srgb,var(--success)_10%,transparent)]">
                <Target className="w-5 h-5 text-[var(--success)]" />
              </div>
              <span className="text-foreground" style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                Metas
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

